<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <div v-else>
      <div class="pa-0 px-1 pb-3 w-full d-flex align-center justify-end">
        <v-dialog
          v-model="isFormOpen"
          width="500"
          persistent
        >
          <template #activator="{ on, attrs }">
            <v-btn
              fab
              small
              :ripple="false"
              color="primary"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>
                {{ icons.mdiPlus }}
              </v-icon>
            </v-btn>
          </template>

          <v-card>
            <v-card-title>
              {{ form.id ? 'その他/調整テンプレート編集' : '新規その他/調整テンプレート' }}
            </v-card-title>

            <v-card-text>
              <v-text-field
                v-model="form.name"
                label="テンプレート名"
                :error="!form.name"
                placeholder="例: その他"
              />

              <div class="text-caption mt-2">
                項目(クロージングの「その他/調整」でこの順に列として挿入されます)
              </div>

              <v-row
                v-for="(item, idx) in form.items"
                :key="`form-item-${idx}`"
                align="center"
              >
                <v-col cols="6">
                  <v-text-field
                    v-model="item.name"
                    label="項目名"
                    :error="!item.name.trim()"
                    placeholder="例: 交通費"
                  />
                </v-col>

                <v-col cols="5">
                  <v-text-field
                    v-model.number="item.amount"
                    label="デフォルト金額"
                    type="number"
                    inputmode="numeric"
                    prefix="¥"
                    placeholder="0"
                  />
                </v-col>

                <v-col
                  cols="1"
                  class="d-flex justify-end"
                >
                  <v-btn
                    icon
                    small
                    :ripple="false"
                    :disabled="form.items.length <= 1"
                    @click="removeItem(idx)"
                  >
                    <v-icon small>
                      {{ icons.mdiClose }}
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn
                text
                small
                color="primary"
                :ripple="false"
                class="mb-2"
                @click="addItem"
              >
                <v-icon
                  small
                  left
                >
                  {{ icons.mdiPlus }}
                </v-icon>
                項目を追加
              </v-btn>

              <v-text-field
                v-model.number="form.position"
                label="表示順"
                type="number"
                placeholder="0"
              />
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                :ripple="false"
                :disabled="!isValid"
                :loading="isSubmitting"
                @click="submit"
              >
                {{ form.id ? '更新' : '作成' }}
              </v-btn>

              <v-btn
                :ripple="false"
                color="blue darken-1"
                text
                @click="cancelForm"
              >
                キャンセル
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>

      <v-data-table
        :headers="headers"
        :items="earningTemplates"
        :header-props="{ sortByText: 'ソート' }"
        hide-default-footer
        disable-pagination
        no-data-text="テンプレートがありません"
      >
        <template #[`item.attributes.items`]="{item}">
          <v-chip
            v-for="(templateItem, idx) in item.attributes.items"
            :key="`${item.id}-item-${idx}`"
            small
            class="ma-1"
          >
            {{ templateItem.name }}
            <span
              v-if="templateItem.amount"
              class="ml-1 text--secondary"
            >
              ¥{{ templateItem.amount.toLocaleString() }}
            </span>
          </v-chip>
        </template>

        <template #[`item.actions`]="{item}">
          <span class="d-flex align-center justify-end">
            <v-btn
              icon
              :ripple="false"
              @click="edit(item)"
            >
              <v-icon small>
                {{ icons.mdiPencilOutline }}
              </v-icon>
            </v-btn>

            <yona-edit-dialog
              title="削除の確認"
              btn-color="error"
              save-text="同意の上で削除"
              @save="destroy(item.id)"
            >
              <template #append-outer-display-name>
                <v-btn
                  icon
                  :ripple="false"
                >
                  <v-icon small>
                    {{ icons.mdiTrashCanOutline }}
                  </v-icon>
                </v-btn>
              </template>

              <template #input>
                <span>
                  削除を実行してもよろしいですか(すでに保存済みのクロージングの項目には影響しません)
                </span>
              </template>
            </yona-edit-dialog>
          </span>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiPlus,
  mdiClose,
  mdiPencilOutline,
  mdiTrashCanOutline,
} from '@mdi/js'
import {
  findIndex,
  sortBy,
  chain,
  map,
} from 'lodash'
import EarningTemplateApi from '@/api/admin/EarningTemplate'
import useCompInit from '@/views/composable/useCompInit'
import YonaEditDialog from '@/views/components/util/YonaEditDialog.vue'

export default {
  components: {
    YonaEditDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { isLoading, initWith } = useCompInit()
    const isFormOpen = ref(false)
    const isSubmitting = ref(false)
    const buildItem = (name = '', amount = 0) => ({ name, amount })
    const form = reactive({
      id: null,
      name: '',
      items: [buildItem()],
      position: 0,
    })
    const earningTemplates = ref([])

    // 項目名が空の行は捨て、同名は先勝ち。金額は未入力なら0
    const normalizeItems = items => chain(items)
      .map(o => buildItem(String(o.name || '').trim(), Number.isFinite(+o.amount) && o.amount !== '' ? +o.amount : 0))
      .filter(o => !!o.name)
      .uniqBy('name')
      .value()

    const hasItems = computed(() => normalizeItems(form.items).length > 0)
    const isValid = computed(() => !!form.name?.trim() && hasItems.value)

    const addItem = () => {
      form.items.push(buildItem())
    }

    const removeItem = idx => {
      form.items.splice(idx, 1)
    }

    const cancelForm = () => {
      isFormOpen.value = false

      form.id = null
      form.name = ''
      form.items = [buildItem()]
      form.position = 0
    }

    const edit = item => {
      isFormOpen.value = true

      form.id = item.id
      form.name = item.attributes.name
      form.items = map(item.attributes.items || [], o => buildItem(o.name, o.amount || 0))
      if (form.items.length === 0) form.items = [buildItem()]
      form.position = item.attributes.position || 0
    }

    const showErrorMessages = err => {
      const messages = err.response?.data?.message

      if (messages?.length) {
        vm.$toast.error(messages.join('\n'))
      } else {
        vm.$toast.error(err.message)
      }
    }

    const submit = async () => {
      isSubmitting.value = true
      const isUpdate = !!form.id
      const fnVerb = isUpdate ? 'update' : 'create'
      const {
        id,
        name,
        items,
        position,
      } = form

      const res = await EarningTemplateApi[`${fnVerb}EarningTemplate`]({
        id,
        name: name.trim(),
        items: normalizeItems(items),
        position,
      }).catch(showErrorMessages)

      if (res?.data) {
        const saved = res.data.earningTemplate.data

        if (isUpdate) {
          const idx = findIndex(earningTemplates.value, o => +o.id === +saved.id)

          earningTemplates.value.splice(idx, 1, saved)
        } else {
          earningTemplates.value.push(saved)
        }
        earningTemplates.value = sortBy(earningTemplates.value, [o => o.attributes.position, o => +o.id])

        vm.$toast.success(isUpdate ? 'テンプレートを更新しました' : 'テンプレートを追加しました')
        cancelForm()
      }

      isSubmitting.value = false
    }

    const destroy = async id => {
      const res = await EarningTemplateApi.deleteEarningTemplate(id).catch(showErrorMessages)

      if (res) {
        const idx = findIndex(earningTemplates.value, o => +o.id === +id)
        earningTemplates.value.splice(idx, 1)
        vm.$toast.success('テンプレートを削除しました')
      }
    }

    const getEarningTemplates = async () => {
      const res = await EarningTemplateApi.getEarningTemplates()

      if (res?.status === 200) {
        earningTemplates.value = [...res.data.earningTemplates.data]
      }
    }

    initWith([
      getEarningTemplates(),
    ])

    return {
      // data
      isLoading,
      isFormOpen,
      isSubmitting,
      form,
      earningTemplates,
      headers: [
        {
          text: 'テンプレート名',
          value: 'attributes.name',
        },
        {
          text: '項目(デフォルト金額)',
          value: 'attributes.items',
          sortable: false,
        },
        {
          text: '表示順',
          value: 'attributes.position',
          align: 'center',
        },
        {
          text: '',
          value: 'actions',
          align: 'right',
          sortable: false,
        },
      ],

      // computed
      hasItems,
      isValid,

      // methods
      addItem,
      removeItem,
      cancelForm,
      edit,
      submit,
      destroy,

      icons: {
        mdiPlus,
        mdiClose,
        mdiPencilOutline,
        mdiTrashCanOutline,
      },
    }
  },
}
</script>
